// Copyright 2026, Imprivata, Inc.  All rights reserved.

import type { OidcAppIntegration, OidcDiscoveryResponse } from '@api/types';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import OneTimeSecretField from '@components/oneTimeSecretField/OneTimeSecretField';
import ReadOnlyCredentialRow from '@components/readOnlyCredentialRow/ReadOnlyCredentialRow';
import RedirectUrisField from '@components/redirectUrisField/RedirectUrisField';
import SecretCloseWarningModal from '@components/secretCloseWarningModal/SecretCloseWarningModal';
import { useCloseWithoutCopyGuard } from '@components/secretCloseWarningModal/useCloseWithoutCopyGuard';
import { Button } from '@imprivata-cloud/components';
import { Alert } from '@imprivata-cloud/design-system';
import { Checkbox, Collapse, Spin } from 'antd';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACR_EPCS, ACR_SSO } from '../constants';
import DisableOidcConfirmationModal from './DisableOidcConfirmationModal';
import SettingsToggle from './SettingsToggle';
import type { CreateOidcResult } from '../hooks/useOidcAppConfig';
import { normalizeUris } from '../hooks/useOidcAppConfig';
import './OidcConfigSection.less';

const TRANSLATION_ROOT = 'apps.oidc';

const deriveIssuer = (discoveryData: OidcDiscoveryResponse): string =>
  discoveryData.issuer ?? new URL(discoveryData.authorization_endpoint).origin;

const deriveMetadataUrl = (discoveryData: OidcDiscoveryResponse): string =>
  `${deriveIssuer(discoveryData).replace(/\/+$/, '')}/.well-known/openid-configuration`;

interface OidcReadOnlyCredentialsProps {
  displayConfig: OidcAppIntegration;
  discoveryData: OidcDiscoveryResponse | undefined;
}

const OidcReadOnlyCredentials: React.FC<OidcReadOnlyCredentialsProps> = ({ displayConfig, discoveryData }) => {
  const { t } = useTranslation();
  const metadataUrl = discoveryData && deriveMetadataUrl(discoveryData);
  return (
    <div className="oidc-readonly-credentials">
      <div className="oidc-credential-group">
        <strong className="oidc-credential-group-title">{t(`${TRANSLATION_ROOT}.client-id`)}</strong>
        <ReadOnlyCredentialRow
          label=""
          value={displayConfig.clientId}
          ariaLabel="oidc-client-id"
        />
      </div>

      {discoveryData && metadataUrl && (
        <>
          <div className="oidc-credential-group">
            <strong className="oidc-credential-group-title">{t(`${TRANSLATION_ROOT}.metadata-url`)}</strong>
            <ReadOnlyCredentialRow
              label=""
              value={metadataUrl}
              ariaLabel="oidc-metadata-url"
            />
          </div>

          <div className="oidc-credential-group">
            <strong className="oidc-credential-group-title">{t(`${TRANSLATION_ROOT}.issuer-url`)}</strong>
            <ReadOnlyCredentialRow
              label=""
              value={deriveIssuer(discoveryData)}
              ariaLabel="oidc-issuer-url"
            />
          </div>

          <div className="oidc-credential-group">
            <strong className="oidc-credential-group-title">{t(`${TRANSLATION_ROOT}.endpoints-title`)}</strong>
            <ReadOnlyCredentialRow
              label={t(`${TRANSLATION_ROOT}.authorization-endpoint`)}
              value={discoveryData.authorization_endpoint}
              ariaLabel="oidc-authorization-endpoint"
            />
            <ReadOnlyCredentialRow
              label={t(`${TRANSLATION_ROOT}.token-endpoint`)}
              value={discoveryData.token_endpoint}
              ariaLabel="oidc-token-endpoint"
            />
          </div>
        </>
      )}

      <div className="oidc-credential-group">
        <strong className="oidc-credential-group-title">{t(`${TRANSLATION_ROOT}.auth-context-title`)}</strong>
        <ReadOnlyCredentialRow
          label={t(`${TRANSLATION_ROOT}.acr-sso`)}
          value={ACR_SSO}
          ariaLabel="oidc-acr-sso"
        />
        <ReadOnlyCredentialRow
          label={t(`${TRANSLATION_ROOT}.acr-epcs`)}
          value={ACR_EPCS}
          ariaLabel="oidc-acr-epcs"
        />
      </div>
    </div>
  );
};

export type OidcCommitOutcome = 'created' | 'updated' | 'noop';

export interface OidcConfigSectionHandle {
  commit: () => Promise<OidcCommitOutcome>;
  isValid: () => boolean;
  showSuccessState: boolean;
  requestClose: (onClose: () => void) => void;
}

interface OidcConfigSectionProps {
  tenantId: string;
  appDisplayName: string;
  discoveryData: OidcDiscoveryResponse | undefined;
  oidcConfig: OidcAppIntegration | undefined;
  isOidcLoading: boolean;
  isCollapsedByDefault: boolean;
  showFirstSignInBanner?: boolean;
  onCloseModal: () => void;
  onOidcSuccess: () => void;
  onValidChange?: (isValid: boolean) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  createOidcConfig: (uris: string[], displayName: string) => Promise<CreateOidcResult>;
  updateOidcConfig: (clientId: string, uris: string[]) => Promise<unknown>;
  deleteOidcConfig: (clientId: string) => Promise<unknown>;
}

type OidcConfigLogicProps = Pick<
  OidcConfigSectionProps,
  | 'oidcConfig'
  | 'appDisplayName'
  | 'isOidcLoading'
  | 'onOidcSuccess'
  | 'onValidChange'
  | 'onDirtyChange'
  | 'createOidcConfig'
  | 'updateOidcConfig'
  | 'deleteOidcConfig'
>;

function useOidcConfigSection({
  oidcConfig,
  appDisplayName,
  isOidcLoading,
  onOidcSuccess,
  onValidChange,
  onDirtyChange,
  createOidcConfig,
  updateOidcConfig,
  deleteOidcConfig,
}: OidcConfigLogicProps) {
  const { emitError } = useErrorContext();
  const guard = useCloseWithoutCopyGuard();

  const isConfigured = !!oidcConfig;
  const [checked, setChecked] = useState(isConfigured);
  const [redirectUris, setRedirectUris] = useState<string[]>(
    oidcConfig?.redirectUris?.length ? oidcConfig.redirectUris : [''],
  );
  const [areUrisValid, setAreUrisValid] = useState(isConfigured);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const [decryptedSecret, setDecryptedSecret] = useState<string>('');
  const [secretMasked, setSecretMasked] = useState(false);
  const [createdConfig, setCreatedConfig] = useState<OidcAppIntegration | undefined>(oidcConfig);
  const [updateAlert, setUpdateAlert] = useState(false);
  const updateAlertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deletedRef = useRef(false);

  const handleCheckboxChange = useCallback(
    (e: { target: { checked: boolean } }) => {
      if (!e.target.checked && (isConfigured || createdConfig)) {
        setShowDisableModal(true);
      } else {
        setChecked(e.target.checked);
      }
    },
    [isConfigured, createdConfig],
  );

  const handleDisableConfirm = useCallback(async () => {
    const clientId = createdConfig?.clientId ?? oidcConfig?.clientId;
    if (!clientId) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteOidcConfig(clientId);
    } catch (e) {
      setIsDeleting(false);
      emitError(new AppError(e as ApiError));
      return;
    }
    setIsDeleting(false);
    deletedRef.current = true;
    setCreatedConfig(undefined);
    setChecked(false);
    setRedirectUris(['']);
    setAreUrisValid(false);
    setShowDisableModal(false);
    setSuccessState(false);
    setDecryptedSecret('');
    setSecretMasked(true);
    guard.reset();
  }, [createdConfig, oidcConfig, deleteOidcConfig, guard, emitError]);

  const handleDisableCancel = useCallback(() => {
    setShowDisableModal(false);
    setChecked(true);
  }, []);

  const commit = useCallback(async (): Promise<OidcCommitOutcome> => {
    if (!checked) {
      return 'noop';
    }

    const existingClientId = createdConfig?.clientId ?? oidcConfig?.clientId;

    if (existingClientId) {
      const prevUris = (createdConfig ?? oidcConfig)?.redirectUris ?? [];
      const sortedCurrent = normalizeUris(redirectUris).sort((a, b) => a.localeCompare(b));
      const sortedPrev = normalizeUris(prevUris).sort((a, b) => a.localeCompare(b));
      const urisChanged = JSON.stringify(sortedCurrent) !== JSON.stringify(sortedPrev);
      if (!urisChanged) {
        return 'noop';
      }
      await updateOidcConfig(existingClientId, redirectUris);
      const normalized = normalizeUris(redirectUris);
      setCreatedConfig(prev => (prev ? { ...prev, redirectUris: normalized } : prev));
      if (updateAlertTimerRef.current) {
        clearTimeout(updateAlertTimerRef.current);
      }
      setUpdateAlert(true);
      updateAlertTimerRef.current = setTimeout(() => setUpdateAlert(false), 3000);
      return 'updated';
    }

    const result = await createOidcConfig(redirectUris, appDisplayName);
    setCreatedConfig({
      clientId: result.createResponse.clientId,
      redirectUris: result.createResponse.redirectUris,
      tokenEndpointAuthMethod: result.createResponse.tokenEndpointAuthMethod,
      productId: result.createResponse.productId,
    });
    setDecryptedSecret(result.decryptedSecret);
    setSuccessState(true);
    onOidcSuccess();
    return 'created';
  }, [
    checked,
    redirectUris,
    createdConfig,
    oidcConfig,
    appDisplayName,
    createOidcConfig,
    updateOidcConfig,
    onOidcSuccess,
  ]);

  useEffect(() => () => {
    if (updateAlertTimerRef.current) {
      clearTimeout(updateAlertTimerRef.current);
    }
  }, []);

  // F1: sync state when oidcConfig prop arrives after mount (cold-load race).
  // Fires when the parent's query resolves after OidcConfigSection already
  // mounted with oidcConfig=undefined (isOidcLoading was true). Only applies
  // when local state still reflects the undefined-derived initial values.
  useEffect(() => {
    if (oidcConfig && !createdConfig && !checked && !deletedRef.current) {
      setChecked(true);
      setRedirectUris(oidcConfig.redirectUris?.length ? oidcConfig.redirectUris : ['']);
      setAreUrisValid(true);
      setCreatedConfig(oidcConfig);
    }
  }, [oidcConfig, createdConfig, checked]);

  const isValid = useCallback(() => {
    if (!checked) {
      return true;
    }
    return areUrisValid;
  }, [checked, areUrisValid]);

  useEffect(() => {
    onValidChange?.(isOidcLoading ? false : isValid());
  }, [isOidcLoading, isValid, onValidChange]);

  const savedClientId = createdConfig?.clientId ?? oidcConfig?.clientId;
  const savedUris = (createdConfig ?? oidcConfig)?.redirectUris ?? [];
  const isDirty =
    checked &&
    (!savedClientId ||
      JSON.stringify(normalizeUris(redirectUris).sort((a, b) => a.localeCompare(b))) !==
        JSON.stringify(normalizeUris(savedUris).sort((a, b) => a.localeCompare(b))));

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const requestClose = useCallback(
    (onClose: () => void) => {
      guard.requestClose(onClose);
    },
    [guard],
  );

  return {
    checked,
    redirectUris,
    setRedirectUris,
    areUrisValid,
    setAreUrisValid,
    showDisableModal,
    isDeleting,
    successState,
    decryptedSecret,
    secretMasked,
    setSecretMasked,
    createdConfig,
    updateAlert,
    setUpdateAlert,
    guard,
    displayConfig: createdConfig ?? oidcConfig,
    handleCheckboxChange,
    handleDisableConfirm,
    handleDisableCancel,
    commit,
    isValid,
    requestClose,
  };
}

const OidcConfigSection = forwardRef<OidcConfigSectionHandle, OidcConfigSectionProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const { appDisplayName, discoveryData, isOidcLoading, isCollapsedByDefault, showFirstSignInBanner = false, onCloseModal } = props;

    const {
      checked,
      redirectUris, setRedirectUris,
      areUrisValid: _areUrisValid, setAreUrisValid,
      showDisableModal,
      isDeleting,
      successState,
      decryptedSecret,
      secretMasked, setSecretMasked,
      displayConfig,
      updateAlert, setUpdateAlert,
      guard,
      handleCheckboxChange,
      handleDisableConfirm,
      handleDisableCancel,
      commit,
      isValid,
      requestClose,
    } = useOidcConfigSection(props);

    useImperativeHandle(
      ref,
      () => ({ commit, isValid, showSuccessState: successState, requestClose }),
      [commit, isValid, successState, requestClose],
    );

    if (isOidcLoading) {
      return (
        <div className="oidc-config-section-loading">
          <Spin />
        </div>
      );
    }

    return (
      <div className={`oidc-config-section${successState ? ' oidc-config-section-success' : ''}`}>
        {!successState && (
          <>
            <div className="oidc-config-section-header">
              <h3 className="h5 section-title">{t(`${TRANSLATION_ROOT}.section-title`)}</h3>
            </div>

            {updateAlert && (
              <Alert
                status="success"
                title={t(`${TRANSLATION_ROOT}.update-success`)}
                className="oidc-update-alert"
                onClose={() => setUpdateAlert(false)}
              />
            )}

            <div className="oidc-checkbox-block">
              <Checkbox
                checked={checked}
                onChange={handleCheckboxChange}
                className="oidc-config-section-checkbox"
                aria-label={t(`${TRANSLATION_ROOT}.checkbox-label`)}
              >
                <span className="oidc-checkbox-label">{t(`${TRANSLATION_ROOT}.checkbox-label`)}</span>
              </Checkbox>
              <p className="oidc-checkbox-subtitle">{t(`${TRANSLATION_ROOT}.checkbox-subtitle`)}</p>
            </div>
          </>
        )}

        {checked && !successState && (
          <Collapse
            ghost
            expandIconPosition="end"
            expandIcon={({ isActive }) => <SettingsToggle isActive={isActive} />}
            className="oidc-config-collapse"
            defaultActiveKey={isCollapsedByDefault ? [] : ['oidc']}
            items={[
              {
                key: 'oidc',
                label: <strong className="h5 section-title">{t(`${TRANSLATION_ROOT}.config-title`)}</strong>,
                forceRender: true,
                children: (
                  <div className="oidc-config-form">
                    {!displayConfig && showFirstSignInBanner && (
                      <Alert
                        status="info"
                        title={t(`${TRANSLATION_ROOT}.first-sign-in-banner-body`)}
                        className="oidc-first-signin-banner"
                      />
                    )}

                    <div className="oidc-redirect-uri-section">
                      <strong className="oidc-redirect-uri-title">
                        {t(`${TRANSLATION_ROOT}.redirect-uri-section-title`)}
                      </strong>
                      <p className="oidc-redirect-uri-helper">
                        {t(`${TRANSLATION_ROOT}.${displayConfig ? 'redirect-uri-section-helper-edit' : 'redirect-uri-section-helper-add'}`)}
                      </p>
                      <RedirectUrisField
                        value={redirectUris}
                        onChange={setRedirectUris}
                        onValidChange={setAreUrisValid}
                        translations={{ addLabel: t(`${TRANSLATION_ROOT}.redirect-uri-add`) }}
                      />
                    </div>

                    {displayConfig && (
                      <>
                        <p className="oidc-configure-hint">{t(`${TRANSLATION_ROOT}.redirect-uri-section-configure-hint`)}</p>
                        <OidcReadOnlyCredentials displayConfig={displayConfig} discoveryData={discoveryData} />
                      </>
                    )}
                  </div>
                ),
              },
            ]}
          />
        )}

        {successState && displayConfig && (
          <div className="oidc-success-state">
            <Alert
              status="success"
              title={t(`${TRANSLATION_ROOT}.success-banner`)}
              className="oidc-success-banner"
            />
            <div className="oidc-summary-row">
              <div className="oidc-summary-col">
                <span className="oidc-summary-label">
                  {t(`${TRANSLATION_ROOT}.summary-display-name`)}
                </span>
                <span className="oidc-summary-value">{appDisplayName}</span>
              </div>
              <div className="oidc-summary-col">
                <span className="oidc-summary-label">
                  {t(`${TRANSLATION_ROOT}.summary-auth-policies`)}
                </span>
                <span className="oidc-summary-value">
                  <span>{t(`${TRANSLATION_ROOT}.acr-sso`)}</span>
                  <span>{t(`${TRANSLATION_ROOT}.acr-epcs`)}</span>
                </span>
              </div>
              <div className="oidc-summary-col">
                <span className="oidc-summary-label">
                  {t(`${TRANSLATION_ROOT}.redirect-uri-section-title`)}
                </span>
                <ul className="oidc-summary-value">
                  {displayConfig.redirectUris.map(uri => (
                    <li key={uri}>{uri}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="oidc-success-divider" />
            <div className="oidc-secret-section">
              <strong className="oidc-secret-section-title">{t(`${TRANSLATION_ROOT}.secret-section-title`)}</strong>
              <p className="oidc-secret-section-helper">{t(`${TRANSLATION_ROOT}.secret-section-helper`)}</p>
              <OneTimeSecretField
                value={decryptedSecret}
                masked={secretMasked}
                onToggleMasked={() => setSecretMasked(m => !m)}
                onCopied={guard.markSecretCopied}
                warningText={t(`${TRANSLATION_ROOT}.secret-warning`)}
                ariaLabel={t(`${TRANSLATION_ROOT}.secret-copy`)}
              />
            </div>
            <div className="oidc-success-divider" />
            <p className="oidc-configure-hint">{t(`${TRANSLATION_ROOT}.redirect-uri-section-configure-hint`)}</p>
            <OidcReadOnlyCredentials displayConfig={displayConfig} discoveryData={discoveryData} />
            <div className="oidc-success-footer">
              <Button
                size="large"
                type="primary"
                onClick={() => guard.requestClose(onCloseModal)}
                label={t(`${TRANSLATION_ROOT}.done`)}
              />
            </div>
          </div>
        )}

        <DisableOidcConfirmationModal
          open={showDisableModal}
          isDeleting={isDeleting}
          onDisable={handleDisableConfirm}
          onCancel={handleDisableCancel}
        />

        <SecretCloseWarningModal
          open={guard.isWarningOpen}
          onConfirm={guard.confirmClose}
          onCancel={guard.cancelClose}
          title={t(`${TRANSLATION_ROOT}.close-warning.title`)}
          message={t(`${TRANSLATION_ROOT}.close-warning.body`)}
          confirmLabel={t(`${TRANSLATION_ROOT}.close-warning.confirm`)}
          cancelLabel={t(`${TRANSLATION_ROOT}.close-warning.cancel`)}
          className="hyperdrive-secret-close-warning"
        />
      </div>
    );
  },
);

OidcConfigSection.displayName = 'OidcConfigSection';

export default OidcConfigSection;
